import React from 'react';
import type { BundlePreviewVideo } from '../../../services/adOpsApi';
import { EmptyState, tableStyles } from '../../ui';

interface VideoPreviewTableProps {
  videos: BundlePreviewVideo[];
  selectedContentIds: Set<string>;
  onToggleVideo: (contentId: string) => void;
}

const subText: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-3)',
  marginTop: 2,
};

const thumb: React.CSSProperties = {
  width: 88,
  height: 50,
  borderRadius: 'var(--radius-xs)',
  background: 'var(--cg-neutral-50)',
  objectFit: 'cover',
  display: 'block',
};

const thumbPlaceholder: React.CSSProperties = {
  ...thumb,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 10,
  color: 'var(--fg-3)',
};

const titleLink: React.CSSProperties = {
  color: 'var(--accent)',
  textDecoration: 'none',
  fontWeight: 500,
};

const VideoPreviewTable: React.FC<VideoPreviewTableProps> = ({
  videos,
  selectedContentIds,
  onToggleVideo,
}) => {
  if (videos.length === 0) {
    return (
      <EmptyState
        brand={false}
        title="No matching videos"
        subtitle="Try a broader topic or different keywords."
        style={{ margin: '24px auto' }}
      />
    );
  }
  return (
    <div style={tableStyles.wrapScroll}>
      <table style={tableStyles.table}>
        <thead>
          <tr>
            <th style={{ ...tableStyles.th, width: 36 }} aria-label="Select" />
            <th style={{ ...tableStyles.th, width: 100 }}>Thumb</th>
            <th style={tableStyles.th}>Title</th>
            <th style={tableStyles.th}>Matched keywords</th>
            <th style={{ ...tableStyles.thNum, width: 100 }}>Duration</th>
          </tr>
        </thead>
        <tbody>
          {videos.map((v) => {
            const ytUrl = v.youtubeVideoId
              ? `https://www.youtube.com/watch?v=${v.youtubeVideoId}`
              : null;
            const thumbUrl = v.youtubeVideoId
              ? `https://i.ytimg.com/vi/${v.youtubeVideoId}/mqdefault.jpg`
              : null;
            return (
              <tr key={v.contentId}>
                <td style={tableStyles.td}>
                  <input
                    type="checkbox"
                    checked={selectedContentIds.has(v.contentId)}
                    onChange={() => onToggleVideo(v.contentId)}
                    aria-label={`Toggle ${v.title}`}
                  />
                </td>
                <td style={tableStyles.td}>
                  {thumbUrl ? (
                    <a href={ytUrl ?? '#'} target="_blank" rel="noreferrer">
                      <img
                        src={thumbUrl}
                        alt=""
                        style={thumb}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </a>
                  ) : (
                    <div style={thumbPlaceholder}>no preview</div>
                  )}
                </td>
                <td style={tableStyles.td}>
                  <div>
                    {ytUrl ? (
                      <a href={ytUrl} target="_blank" rel="noreferrer" style={titleLink}>
                        {v.title}
                      </a>
                    ) : (
                      <span style={{ fontWeight: 500 }}>{v.title}</span>
                    )}
                  </div>
                  <div style={subText}>
                    contentId: {v.contentId}
                    {v.youtubeVideoId && ` · yt: ${v.youtubeVideoId}`}
                  </div>
                </td>
                <td style={tableStyles.td}>
                  {v.matchedKeywords.slice(0, 4).join(', ')}
                  {v.matchedKeywords.length > 4
                    ? `, +${v.matchedKeywords.length - 4}`
                    : ''}
                </td>
                <td style={tableStyles.tdNum}>
                  {v.durationSeconds != null ? `${Math.round(v.durationSeconds)}s` : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default VideoPreviewTable;
